import React, { useState, useEffect } from 'react'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Loader2 } from 'lucide-react'

/**
 * Compose or edit a group announcement.
 *
 * New announcements post immediately — the backend fans out a push and an email
 * to every group member as soon as one is published. Editing an existing
 * announcement sends only the changed text and notifies nobody.
 */
export default function GroupAnnouncementForm({
  onSubmit,
  onCancel,
  loading = false,
  memberCount = 0,
  announcement = null,
}) {
  const isEditing = Boolean(announcement)
  const [title, setTitle] = useState(announcement?.title || '')
  const [content, setContent] = useState(announcement?.content || '')

  // Swap the fields when the admin picks a different announcement to edit
  useEffect(() => {
    setTitle(announcement?.title || '')
    setContent(announcement?.content || '')
  }, [announcement?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!title.trim() || !content.trim()) return
    const payload = { title: title.trim(), content: content.trim() }
    // Only a new announcement publishes — an edit must never re-trigger the fan-out.
    await onSubmit(isEditing ? payload : { ...payload, isPublished: true })
    setTitle('')
    setContent('')
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="border border-gold/30 rounded p-4 mb-4 space-y-4 bg-surface-linen"
    >
      <h3 className="text-base font-semibold text-charcoal">
        {isEditing ? 'Edit Announcement' : 'New Announcement'}
      </h3>

      <div>
        <Label htmlFor="group-announcement-title" className="text-charcoal text-xs">Title *</Label>
        <Input
          id="group-announcement-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Budget review meeting"
          maxLength={200}
          className="mt-1 bg-white border-surface-border text-charcoal"
        />
        <p className="mt-1 text-xs text-charcoal-muted">{title.length}/200 characters</p>
      </div>

      <div>
        <Label htmlFor="group-announcement-content" className="text-charcoal text-xs">Content *</Label>
        <textarea
          id="group-announcement-content"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={4}
          className="mt-1 w-full px-3 py-2 bg-white border border-surface-border rounded text-charcoal placeholder-charcoal-faint focus:outline-none focus:ring-2 focus:ring-gold/50"
          placeholder="Write your announcement here..."
        />
      </div>

      {isEditing ? (
        <p className="text-xs text-charcoal-muted">
          Saving updates the announcement for members. It does not send another notification.
        </p>
      ) : (
        <p className="text-xs text-charcoal-muted">
          Posting sends a push notification and an email to
          {memberCount === 1 ? ' the 1 member' : ` all ${memberCount} members`} of this group,
          immediately. Nobody outside the group can see it.
        </p>
      )}

      <div className="flex items-center justify-end gap-2 pt-2 border-t border-surface-border">
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          disabled={loading || !title.trim() || !content.trim()}
          className="bg-gold text-black hover:bg-gold/90"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {isEditing ? 'Saving...' : 'Posting...'}
            </>
          ) : isEditing ? (
            'Save Changes'
          ) : (
            'Post Announcement'
          )}
        </Button>
      </div>
    </form>
  )
}
